import { useCallback, useEffect, useState } from 'react';
import { Card, Form, InputGroup } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';
import { Checkbox } from '@Components/FormCheck';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Size } from '@src/Components/designConstants';
import { awaitJSON, FlashtalkingLambdaFetch, SheetsLambdaFetch } from '../../../utils/fetch-utils';
import { AdOpsToolProps } from '../adops-constants';

export const FlashtalkingCampaignAdder = ({ utils }: AdOpsToolProps) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const [advertiserId, setAdvertiserId] = useState<number | null>(null);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [campaignName, setCampaignName] = useState('');
  const [company, setCompany] = useState('');
  const [companyOptions, setCompanyOptions] = useState<string[]>();
  const [isManagedService, setIsManagedService] = useState(false);
  const { setError } = utils;

  useEffect(() => {
    if (!companyOptions) {
      (async () => {
        try {
          const res = await SheetsLambdaFetch('/get_company_ids');
          const companies = await awaitJSON<{ ids: string[] }>(res);
          setCompanyOptions(companies.ids);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [companyOptions, setError]);

  const companyDropdownOptions: DropdownOption[] = (companyOptions ?? []).map(value => ({
    value,
    label: value
  }));

  const addFlashtalkingCampaign = useCallback(async () => {
    try {
      setActiveAPICall(true);
      await FlashtalkingLambdaFetch('/addFlashtalkingCampaign', {
        method: 'POST',
        body: JSON.stringify({
          advertiser_id: advertiserId,
          campaign_id: campaignId,
          campaign: campaignName,
          company,
          is_managed_service: isManagedService
        })
      });
      setAdvertiserId(null);
      setCampaignId(null);
      setCampaignName('');
      setCompany('');
      setIsManagedService(false);
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    }
    setActiveAPICall(false);
  }, [advertiserId, campaignId, campaignName, company, isManagedService, setError]);

  return (
    <Card className='ad-ops-card' data-testid='ft-campaign'>
      <div className='ad-ops-card-title'>
        Add Flashtalking Campaign
        <InfoTooltip>
          The company you input should be the all lowercased company ID used in master_companies and
          in our app URLs.
          <br />
          <br />
          You can find the Advertiser Name and ID by selecting your client
          <a
            href='https://admin.flashtalking.net/advertisers/'
            target='_blank'
            rel='noopener noreferrer'
          >
            {' '}
            here.
          </a>
          <br />
          <br />
          You can find the Campaign Name and ID by searching for your client
          <a
            href='https://campaign-manager.flashtalking.net/choose/campaign/'
            target='_blank'
            rel='noopener noreferrer'
          >
            {' '}
            here
          </a>
          .
        </InfoTooltip>
      </div>
      <div className='ad-ops-elem' data-testid='ftc-company'>
        <Dropdown
          size={Size.LG}
          label='Company'

          menuPortalTarget={document.body}
          placeholder='Select or type a company…'
          searchable
          creatable
          clearable
          value={company || null}
          options={companyDropdownOptions}
          onChange={value => setCompany(value ?? '')}
          reactSelectProps={{ formatCreateLabel: input => `Use "${input}"` }}
        />
      </div>
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Advertiser Id</InputGroup.Text>
        <Form.Control
          value={advertiserId ?? ''}
          onChange={e => setAdvertiserId(e.target.value ? parseInt(e.target.value, 10) : null)}
          data-testid='ftc-advertiser-id'
        />
      </InputGroup>
      <InputGroup size='sm' className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Campaign Name</InputGroup.Text>
        <Form.Control
          value={campaignName}
          onChange={e => setCampaignName(e.target.value)}
          data-testid='ftc-campaign-name'
        />
        <InputGroup.Text className='input-label'>Campaign Id</InputGroup.Text>
        <Form.Control
          value={campaignId ?? ''}
          onChange={e => setCampaignId(e.target.value ? parseInt(e.target.value, 10) : null)}
          data-testid='ftc-campaign-id'
        />
      </InputGroup>
      <InputGroup size='sm' className='ad-ops-elem ad-ops-check-inputs'>
        <InputGroup.Text className='input-label'>Is Managed Service/DCO Campaign?</InputGroup.Text>
        <Checkbox
          checked={isManagedService}
          onChange={() => setIsManagedService(!isManagedService)}
        />
      </InputGroup>
      <Button
        size={Size.LG}
        className='ad-ops-elem'
        loading={activeAPICall}
        onClick={addFlashtalkingCampaign}
        data-testid='ft-campaign-add'
      >
        Add
      </Button>
    </Card>
  );
};
